import React, { useState, useEffect } from 'react';
import DashboardLayout from "../layouts/DashboardLayout";
import InfoModal from "../layouts/InfoModal";
import PaginatedTableContainer from "../containers/PaginatedTableContainer";
import { getAnnouncement } from "../../../mobile/api/AnnouncementApi";
import { getAllAnnouncements, createAnnouncement, updateAnnouncement, deleteAnnouncement } from "../../api/AdminApi";

const AnnouncementManagementPage = () => {
    const [announcements, setAnnouncements] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);

    // 모달 관련 상태
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
    const [modalLoading, setModalLoading] = useState(false);
    const [isCreateMode, setIsCreateMode] = useState(false);
    
    // 등록/수정 폼
    const [formTitle, setFormTitle] = useState('');
    const [formContent, setFormContent] = useState('');

    const fetchAnnouncements = async (page = 0) => {
        setLoading(true);
        try {
            const response = await getAllAnnouncements(page, 20);
            setAnnouncements(response.content);
            setCurrentPage(response.currentPage);
            setTotalPages(response.totalPages);
            setTotalElements(response.totalElements);
        } catch (error) {
            console.error('공지사항 목록 조회 실패:', error);
            alert('공지사항 목록을 불러오는데 실패했습니다.');
        } finally {
            setLoading(false);
        }
    };

    const handleRowClick = async (announcement) => {
        setModalLoading(true);
        setModalOpen(true);
        setIsCreateMode(false);
        
        try {
            const announcementDetail = await getAnnouncement(announcement.id);
            setSelectedAnnouncement(announcementDetail);
            setFormTitle(announcementDetail.title);
            setFormContent(announcementDetail.content);
        } catch (error) {
            console.error('공지사항 상세 조회 실패:', error);
            alert('공지사항 정보를 불러오는데 실패했습니다.');
            setModalOpen(false);
        } finally {
            setModalLoading(false);
        }
    };

    const handleCreateClick = () => {
        setSelectedAnnouncement(null);
        setFormTitle('');
        setFormContent('');
        setIsCreateMode(true);
        setModalOpen(true);
    };

    const handleSubmit = async () => {
        if (!formTitle.trim() || !formContent.trim()) {
            alert('제목과 내용을 모두 입력해주세요.');
            return;
        }

        try {
            if (isCreateMode) {
                await createAnnouncement(formTitle, formContent);
                alert('공지사항이 등록되었습니다.');
            } else {
                await updateAnnouncement(selectedAnnouncement.id, formTitle, formContent);
                alert('공지사항이 수정되었습니다.');
            }
            setModalOpen(false);
            fetchAnnouncements(currentPage);
        } catch (error) {
            console.error('공지사항 저장 실패:', error);
            alert('공지사항 저장에 실패했습니다.');
        }
    };

    const handleDelete = async () => {
        const confirmed = window.confirm(`정말로 "${selectedAnnouncement.title}" 공지사항을 삭제하시겠습니까? 이 동작은 되돌릴 수 없습니다.`);
        if (!confirmed) return;
        
        try {
            await deleteAnnouncement(selectedAnnouncement.id);
            alert('공지사항이 삭제되었습니다.');
            setModalOpen(false);
            fetchAnnouncements(currentPage);
        } catch (error) {
            console.error('공지사항 삭제 실패:', error);
            alert('공지사항 삭제에 실패했습니다.');
        }
    };

    useEffect(() => {
        fetchAnnouncements();
    }, []);

    const handlePageChange = (page) => {
        if (page >= 0 && page < totalPages) {
            setCurrentPage(page);
            fetchAnnouncements(page);
        }
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleString('ko-KR');
    };

    // 테이블 컬럼 정의 (Announcement 엔터티 기준)
    const columns = [
        { header: 'id', field: 'id', minWidth: 'min-w-12' },
        { header: 'title', field: 'title', minWidth: 'min-w-48' },
        { 
            header: 'content', 
            field: 'content', 
            minWidth: 'min-w-32',
            render: (announcement) => {
                if (!announcement.content) return '';
                return announcement.content.length > 30 ? `${announcement.content.substring(0, 30)}...` : announcement.content;
            }
        },
        { 
            header: 'createdAt', 
            field: 'createdAt', 
            minWidth: 'min-w-32',
            render: (announcement) => formatDateTime(announcement.createdAt)
        },
        { 
            header: 'updatedAt', 
            field: 'updatedAt', 
            minWidth: 'min-w-32',
            render: (announcement) => formatDateTime(announcement.updatedAt)
        }
    ];

    return (
        <DashboardLayout>
            <div className="p-3">
                <h1 className="text-lg font-bold mb-3">공지사항 관리</h1>

                {/* 등록 버튼 */}
                <div className="mb-3">
                    <button 
                        onClick={handleCreateClick}
                        className="px-3 py-1 bg-gray-200 border border-gray-400 hover:bg-gray-300 text-xs"
                    >
                        공지사항 등록
                    </button>
                </div>

                {/* 상단 정보 */}
                <div className="mb-2 text-xs text-gray-600">
                    총 {totalElements}개 | {currentPage + 1}/{totalPages} 페이지
                </div>

                {/* 페이지네이션 테이블 */}
                <PaginatedTableContainer
                    columns={columns}
                    data={announcements}
                    loading={loading}
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    onRowClick={handleRowClick}
                />

                {/* 공지사항 상세/등록/수정 모달 */}
                <InfoModal 
                    isOpen={modalOpen} 
                    onClose={() => setModalOpen(false)} 
                    title={isCreateMode ? '공지사항 등록' : `공지사항 수정 - ${selectedAnnouncement?.title || ''}`}
                    width="w-[600px]"
                >
                    {modalLoading ? (
                        <div className="text-center py-4">공지사항 정보를 불러오는 중...</div>
                    ) : (
                        <div className="text-xs space-y-4">
                            {/* 공지사항 정보 (수정 모드가 아닌 경우에만) */}
                            {!isCreateMode && selectedAnnouncement && (
                                <div>
                                    <h3 className="font-bold mb-2 text-sm">공지사항 정보</h3>
                                    <table className="w-full border border-gray-400">
                                        <tbody>
                                            <tr><td className="border border-gray-400 px-2 py-1 bg-gray-100 w-24">id</td><td className="border border-gray-400 px-2 py-1">{selectedAnnouncement.id}</td></tr>
                                            <tr><td className="border border-gray-400 px-2 py-1 bg-gray-100">createdAt</td><td className="border border-gray-400 px-2 py-1">{formatDateTime(selectedAnnouncement.createdAt)}</td></tr>
                                            <tr><td className="border border-gray-400 px-2 py-1 bg-gray-100">updatedAt</td><td className="border border-gray-400 px-2 py-1">{formatDateTime(selectedAnnouncement.updatedAt)}</td></tr>
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {/* 공지사항 수정/등록 폼 */}
                            <div>
                                <h3 className="font-bold mb-2 text-sm">{isCreateMode ? '공지사항 등록' : '공지사항 수정'}</h3>
                                <div className="space-y-2">
                                    <div>
                                        <label className="block mb-1 text-xs font-medium">제목</label>
                                        <input 
                                            type="text" 
                                            value={formTitle}
                                            onChange={(e) => setFormTitle(e.target.value)}
                                            className="w-full px-2 py-1 border border-gray-300 text-xs"
                                            placeholder="공지사항 제목을 입력하세요"
                                        />
                                    </div>
                                    <div>
                                        <label className="block mb-1 text-xs font-medium">내용</label>
                                        <textarea 
                                            value={formContent}
                                            onChange={(e) => setFormContent(e.target.value)}
                                            rows="10"
                                            className="w-full px-2 py-1 border border-gray-300 text-xs"
                                            placeholder="공지사항 내용을 입력하세요"
                                        />
                                    </div>
                                    <div className="flex gap-2">
                                        <button 
                                            onClick={handleSubmit}
                                            className="px-3 py-1 bg-blue-200 border border-gray-400 hover:bg-blue-300"
                                        >
                                            {isCreateMode ? '등록' : '수정'}
                                        </button>
                                        {!isCreateMode && (
                                            <button 
                                                onClick={handleDelete}
                                                className="px-3 py-1 bg-red-200 border border-gray-400 hover:bg-red-300 text-red-800"
                                            >
                                                삭제
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </InfoModal>
            </div>
        </DashboardLayout>
    );
};

export default AnnouncementManagementPage;
